import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Archive, ChevronRight, Folder, FolderInput, FolderPlus, MoreHorizontal, Pencil, Pin, Trash2 } from 'lucide-react';
import { classNames } from '../../utils/classNames';
import { useI18n } from '../../hooks/useI18n';
import { Modal } from '../../components/ui/Modal';
import { Input } from '../../components/ui/Input';
import { Button } from '../../components/ui/Button';

const useIsMobile = () => {
  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== 'undefined' ? window.matchMedia('(max-width: 767px)').matches : false
  );

  useEffect(() => {
    const media = window.matchMedia('(max-width: 767px)');
    const onChange = () => setIsMobile(media.matches);
    onChange();
    media.addEventListener('change', onChange);
    return () => media.removeEventListener('change', onChange);
  }, []);

  return isMobile;
};

const FolderList = ({ item, folders, onMoveToFolder, onClose, className = '' }) => {
  const { t } = useI18n();

  return (
    <div
      className={classNames(
        'w-[220px] overflow-hidden rounded-2xl border border-neutral-200 bg-white py-1 shadow-[0_12px_40px_rgba(0,0,0,0.12)] dark:border-neutral-800 dark:bg-neutral-950',
        className
      )}
    >
      <button
        type="button"
        onClick={() => {
          onMoveToFolder?.('__new__');
          onClose?.();
        }}
        className="flex w-full items-center gap-2 px-3.5 py-2.5 text-start text-sm text-neutral-700 hover:bg-neutral-50 dark:text-neutral-200 dark:hover:bg-neutral-900"
      >
        <FolderPlus size={14} className="opacity-70" />
        {t('folderNew')}
      </button>
      {folders.length > 0 ? (
        <>
          <div className="my-1 border-t border-neutral-200 dark:border-neutral-800" />
          {folders.map((folder) => (
            <button
              key={folder.id}
              type="button"
              onClick={() => {
                onMoveToFolder?.(folder.id);
                onClose?.();
              }}
              className="flex w-full items-center gap-2 px-3.5 py-2.5 text-start text-sm hover:bg-neutral-50 dark:hover:bg-neutral-900"
            >
              <Folder
                size={14}
                className="shrink-0 opacity-80"
                style={folder.color ? { color: folder.color } : undefined}
              />
              <span className="truncate text-neutral-800 dark:text-neutral-200">{folder.name}</span>
            </button>
          ))}
        </>
      ) : (
        <div className="px-3.5 py-2 text-xs text-neutral-400">{t('folderEmpty')}</div>
      )}
      {item.folderId ? (
        <button
          type="button"
          onClick={() => {
            onMoveToFolder?.(null);
            onClose?.();
          }}
          className="flex w-full items-center border-t border-neutral-200 px-3.5 py-2.5 text-start text-sm text-neutral-500 hover:bg-neutral-50 dark:border-neutral-800 dark:hover:bg-neutral-900"
        >
          {t('folderRemoveFrom')}
        </button>
      ) : null}
    </div>
  );
};

const MenuPanel = ({
  item,
  folders = [],
  onEdit,
  onPin,
  onArchive,
  onDelete,
  onMoveToFolder,
  onClose,
  isMobile = false
}) => {
  const { t } = useI18n();
  const [folderOpen, setFolderOpen] = useState(false);
  const closeTimerRef = useRef(null);
  const archiveLabel = item.type === 'conversation' ? t('recentArchiveChat') : t('recentArchive');

  const openFolders = () => {
    if (closeTimerRef.current) {
      window.clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
    setFolderOpen(true);
  };

  const scheduleCloseFolders = () => {
    if (isMobile) {
      return;
    }
    closeTimerRef.current = window.setTimeout(() => {
      setFolderOpen(false);
      closeTimerRef.current = null;
    }, 140);
  };

  useEffect(
    () => () => {
      if (closeTimerRef.current) {
        window.clearTimeout(closeTimerRef.current);
      }
    },
    []
  );

  const items = [
    {
      key: 'edit',
      label: t('recentEditTitle'),
      icon: Pencil,
      onClick: onEdit
    },
    {
      key: 'pin',
      label: item.isPinned ? t('recentUnpin') : t('recentPin'),
      icon: Pin,
      onClick: onPin
    },
    {
      key: 'move',
      label: t('folderMoveTo'),
      icon: FolderInput,
      submenu: true
    },
    {
      key: 'archive',
      label: archiveLabel,
      icon: Archive,
      onClick: onArchive
    },
    {
      key: 'delete',
      label: t('recentDelete'),
      icon: Trash2,
      onClick: onDelete,
      danger: true
    }
  ];

  return (
    <div className="relative w-[220px] rounded-2xl border border-neutral-200 bg-white py-1 shadow-[0_12px_40px_rgba(0,0,0,0.12)] dark:border-neutral-800 dark:bg-neutral-950">
      {items.map((entry) => {
        const Icon = entry.icon;
        if (entry.submenu) {
          return (
            <div
              key={entry.key}
              className="relative"
              onMouseEnter={isMobile ? undefined : openFolders}
              onMouseLeave={isMobile ? undefined : scheduleCloseFolders}
            >
              <button
                type="button"
                onClick={() => {
                  if (isMobile) {
                    setFolderOpen((value) => !value);
                  }
                }}
                className={classNames(
                  'flex w-full items-center gap-3 px-3.5 py-2.5 text-start text-sm text-neutral-700 transition dark:text-neutral-200',
                  folderOpen
                    ? 'bg-neutral-50 dark:bg-neutral-900'
                    : 'hover:bg-neutral-50 dark:hover:bg-neutral-900'
                )}
              >
                <Icon size={16} className="shrink-0 opacity-80 rtl:-scale-x-100" />
                <span className="flex-1 font-medium">{entry.label}</span>
                <ChevronRight size={14} className="opacity-50 rtl:rotate-180" />
              </button>
              {folderOpen && !isMobile ? (
                <div
                  className="absolute top-0 z-10 start-full ps-1"
                  onMouseEnter={openFolders}
                  onMouseLeave={scheduleCloseFolders}
                >
                  <FolderList
                    item={item}
                    folders={folders}
                    onMoveToFolder={onMoveToFolder}
                    onClose={onClose}
                  />
                </div>
              ) : null}
              {folderOpen && isMobile ? (
                <div className="px-2 pb-1">
                  <FolderList
                    item={item}
                    folders={folders}
                    onMoveToFolder={onMoveToFolder}
                    onClose={onClose}
                    className="w-full shadow-none"
                  />
                </div>
              ) : null}
            </div>
          );
        }

        return (
          <button
            key={entry.key}
            type="button"
            onClick={() => {
              entry.onClick?.();
              onClose?.();
            }}
            className={classNames(
              'flex w-full items-center gap-3 px-3.5 py-2.5 text-start text-sm transition',
              entry.danger
                ? 'bg-red-50 text-red-600 hover:bg-red-100 dark:bg-red-950/40 dark:text-red-400 dark:hover:bg-red-950/60'
                : 'text-neutral-700 hover:bg-neutral-50 dark:text-neutral-200 dark:hover:bg-neutral-900'
            )}
          >
            <Icon size={16} className="shrink-0 opacity-80" />
            <span className="flex-1 font-medium">{entry.label}</span>
          </button>
        );
      })}
    </div>
  );
};

export const RecentItemMenu = ({
  item,
  folders = [],
  disabled = false,
  onRename,
  onTogglePin,
  onArchive,
  onDelete,
  onMoveToFolder
}) => {
  const { t } = useI18n();
  const isMobile = useIsMobile();
  const [open, setOpen] = useState(false);
  const [renameOpen, setRenameOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [title, setTitle] = useState(item.title || '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const rootRef = useRef(null);
  const buttonRef = useRef(null);
  const [desktopPos, setDesktopPos] = useState({ top: 0, left: 0 });

  useEffect(() => {
    if (!open || isMobile) return undefined;
    const onPointerDown = (event) => {
      if (!rootRef.current?.contains(event.target) && !buttonRef.current?.contains(event.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onPointerDown);
    return () => document.removeEventListener('mousedown', onPointerDown);
  }, [isMobile, open]);

  useEffect(() => {
    if (!open || isMobile || !buttonRef.current) return;
    const rect = buttonRef.current.getBoundingClientRect();
    const menuWidth = 220;
    const left = Math.min(Math.max(8, rect.right - menuWidth), window.innerWidth - menuWidth - 8);
    const top = Math.min(rect.bottom + 6, window.innerHeight - 280);
    setDesktopPos({ top, left });
  }, [isMobile, open]);

  const closeMenu = () => setOpen(false);

  const handleRenameSubmit = async (event) => {
    event.preventDefault();
    const trimmed = title.trim();
    if (!trimmed) {
      setError(t('chatTitleRequired'));
      return;
    }
    setSaving(true);
    setError('');
    try {
      await onRename?.(trimmed);
      setRenameOpen(false);
    } catch (err) {
      setError(err.message || t('recentActionFailed'));
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteConfirm = async () => {
    setSaving(true);
    setError('');
    try {
      await onDelete?.();
      setDeleteOpen(false);
    } catch (err) {
      setError(err.message || t('recentActionFailed'));
    } finally {
      setSaving(false);
    }
  };

  const menuActions = {
    onEdit: () => {
      setTitle(item.title || '');
      setError('');
      setRenameOpen(true);
    },
    onPin: () => onTogglePin?.(),
    onArchive: () => onArchive?.(),
    onDelete: () => {
      setError('');
      setDeleteOpen(true);
    },
    onMoveToFolder,
    onClose: closeMenu
  };

  return (
    <>
      <button
        ref={buttonRef}
        type="button"
        disabled={disabled}
        onClick={(event) => {
          event.preventDefault();
          event.stopPropagation();
          setOpen((value) => !value);
        }}
        className={classNames(
          'inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-neutral-400 transition',
          'hover:bg-neutral-200/70 hover:text-neutral-700 dark:hover:bg-neutral-800 dark:hover:text-neutral-200',
          'opacity-100 md:opacity-0 md:group-hover:opacity-100 md:focus:opacity-100',
          open && '!opacity-100 bg-neutral-200/70 text-neutral-700 dark:bg-neutral-800'
        )}
        aria-label={t('more')}
        aria-expanded={open}
      >
        <MoreHorizontal size={16} />
      </button>

      {open && !isMobile && typeof document !== 'undefined'
        ? createPortal(
            <div
              ref={rootRef}
              className="fixed z-[80]"
              style={{ top: desktopPos.top, left: desktopPos.left }}
            >
              <MenuPanel item={item} folders={folders} isMobile={false} {...menuActions} />
            </div>,
            document.body
          )
        : null}

      {open && isMobile && typeof document !== 'undefined'
        ? createPortal(
            <div className="fixed inset-0 z-[80] md:hidden">
              <button
                type="button"
                className="absolute inset-0 bg-black/40"
                aria-label={t('close')}
                onClick={closeMenu}
              />
              <div className="absolute inset-x-0 bottom-0 p-3 pb-[max(0.75rem,env(safe-area-inset-bottom))]">
                <div className="mx-auto max-w-md">
                  <div className="mb-2 truncate px-1 text-center text-xs text-neutral-500">{item.title}</div>
                  <MenuPanel item={item} folders={folders} isMobile {...menuActions} />
                </div>
              </div>
            </div>,
            document.body
          )
        : null}

      <Modal
        open={renameOpen}
        onClose={() => !saving && setRenameOpen(false)}
        closeDisabled={saving}
        title={t('recentEditTitle')}
        panelClassName="max-w-md rounded-2xl p-5"
      >
        <form onSubmit={handleRenameSubmit} className="space-y-4">
          <Input
            label={t('recentTitleLabel')}
            value={title}
            onChange={(event) => setTitle(event.target.value)}
            autoFocus
          />
          {error ? <p className="text-sm text-red-500">{error}</p> : null}
          <div className="flex justify-end gap-2">
            <Button type="button" variant="ghost" disabled={saving} onClick={() => setRenameOpen(false)}>
              {t('cancel')}
            </Button>
            <Button type="submit" disabled={saving}>
              {t('save')}
            </Button>
          </div>
        </form>
      </Modal>

      <Modal
        open={deleteOpen}
        onClose={() => !saving && setDeleteOpen(false)}
        closeDisabled={saving}
        title={t('recentDelete')}
        panelClassName="max-w-md rounded-2xl p-5"
      >
        <p className="mb-4 text-sm leading-6 text-neutral-600 dark:text-neutral-300">
          {t('recentDeleteConfirm', { title: item.title || '' })}
        </p>
        {error ? <p className="mb-3 text-sm text-red-500">{error}</p> : null}
        <div className="flex justify-end gap-2">
          <Button type="button" variant="ghost" disabled={saving} onClick={() => setDeleteOpen(false)}>
            {t('cancel')}
          </Button>
          <Button type="button" disabled={saving} onClick={handleDeleteConfirm} className="!bg-red-600 !text-white hover:!bg-red-700">
            {t('recentDelete')}
          </Button>
        </div>
      </Modal>
    </>
  );
};
